import { MenuPositionType, SidebarMenuType } from '@vedalib/editor/lib/brand'
import { CONFIG_COURSE } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { useEmbedParams } from 'hooks/useEmbedParams'
import { useEmbedResizeNotifier } from 'hooks/useEmbedResizeNotifier'
import React, { useCallback, useMemo, useRef } from 'react'

import { DisplayError } from 'components/DataFallback'
import { DataFallbackTypeEnum } from 'components/DataFallback/types'
import { COURSE_TOOLTIP } from 'components/Editor/EditorElements/courseKit/portalsCurseKit'
import { DocumentTitle } from 'components/LayoutPage'
import { PortalTarget } from 'components/uiKit/Portal'
import { ContractType } from 'gql/__generated__/graphql'
import { useElementStyleCss, useFontCss, useBrandTheme } from 'services/Branding/hooks'
import { useProjectT } from 'services/ProjectLabels'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getCourseState,
  getPreviewMode,
  getDeviceMode,
  getEditorMode,
  getProject,
  getSection,
} from 'services/Store/Project/selectors'
import { focusMain } from 'utils/accessibility'
import { getFaviconOrDefault } from 'utils/files'
import { testProps } from 'utils/test/qaData'

import SkipToContentLinkStyled from '../Editor/cource/components/SkipToContentLinkStyled'
import TrialLogo from '../Editor/cource/components/TrialLogo'
import { CourseHeaderContainer } from '../Editor/cource/controls/CourseHeader'
import CourseSidebarContainer from '../Editor/cource/controls/CourseSidebar'
import FinishModal from '../Editor/cource/controls/FinishModal'
import CourseSection from '../Editor/cource/renderEntry/CourseSection'
import IncompleteElementModal from '../Preview/IncompleteElementModal/IncompleteElementModal'
import * as s from './Course.module.scss'
import { useCourseHotkeys } from './useCourseHotkeys'
import useSidebarFocusManagement from './useSidebarFocusManagement'

interface ICourseProps {
  isPartView?: boolean
}

const ALLOW_MODES_FAVICON = [PreviewMode.preview, PreviewMode.publicLink, PreviewMode.scorm]
const DIVIDER = ' - '

const Course: React.FC<ICourseProps> = ({ isPartView }) => {
  const embedParams = useEmbedParams()
  const deviceMode = useProjectContext(getDeviceMode)
  const previewMode = useProjectContext(getPreviewMode)
  const editorMode = useProjectContext(getEditorMode)
  const courseState = useProjectContext(getCourseState)
  const contractType = useProjectContext(getProject)?.company?.contractType
  const mode = useMemo(
    () => ({ deviceMode, previewMode, editorMode }),
    [deviceMode, previewMode, editorMode],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const styles = useElementStyleCss(CONFIG_COURSE.sidebar.type, mode.deviceMode)
  const section = useProjectContext(getSection)
  const cover = useProjectContext(getSection, { cover: true })
  const project = useProjectContext(getProject)
  const pt = useProjectT()

  // Data for focus management
  const isDrawer = styles.menu.type === SidebarMenuType.drawer
  const { headerRef, sidebarRef } = useSidebarFocusManagement(isDrawer)

  const font = useFontCss(deviceMode)
  const theme = useBrandTheme()

  const showTrialLogo = contractType === ContractType.TRIAL
  const menuPosition = styles.menu.position as MenuPositionType
  const sectionName = section?.name || cover?.name
  const shouldShowCustomFavicon = ALLOW_MODES_FAVICON.includes(previewMode)
  const faviconUrl = shouldShowCustomFavicon ? getFaviconOrDefault(theme?.favicon) : undefined
  const docTitle = sectionName ? project?.name.concat(DIVIDER, sectionName) : project?.name

  const handleSkipToContent = useCallback(() => {
    focusMain()
  }, [])

  useCourseHotkeys()
  useEmbedResizeNotifier(containerRef, { enabled: embedParams.isEmbedMode })

  if (embedParams.enabledSections?.length && !section) {
    return <DisplayError backUrl={null} title='Section' type={DataFallbackTypeEnum.notFound} />
  }

  return (
    <div
      className={s.root}
      id='course-root'
      style={font.base}
      {...testProps({ el: 'course', mode: previewMode, label: docTitle })}
    >
      {docTitle && <DocumentTitle faviconUrl={faviconUrl} title={docTitle} />}
      <SkipToContentLinkStyled $font={font.base} onClick={handleSkipToContent}>
        {pt('navigationButton.skipToContent')}
      </SkipToContentLinkStyled>
      {/* eslint-disable-next-line react/forbid-component-props */}
      <PortalTarget className={s.tooltipPortal} id={COURSE_TOOLTIP} />
      {!embedParams.hideHeader && (
        <div className={s.header} ref={headerRef}>
          <CourseHeaderContainer isPartView={isPartView} mode={mode} />
        </div>
      )}
      <div className={cn(s.body, { [s.positionsRight]: menuPosition === MenuPositionType.right })}>
        {!embedParams.hideNav && <CourseSidebarContainer mode={mode} ref={sidebarRef} />}
        <div className={s.contentWrapper} ref={containerRef}>
          {showTrialLogo && !embedParams.isEmbedMode && (
            <div className={s.trial}>
              <TrialLogo />
            </div>
          )}
          <CourseSection mode={mode} />
        </div>
      </div>
      {courseState.status === 'finished' &&
        !embedParams.hideFooter &&
        !embedParams.enabledSections?.length &&
        !embedParams.enabledBlocks?.length &&
        // Portal V2 inline player показывает свой CompletionDialog с
        // сертификатом/возвратом в портал. Внутрикурсовой FinishModal в этом
        // контексте — дубль, его FocusTrap ещё и глушит клики на CompletionDialog.
        !(window as unknown as { __PORTAL_V2__?: boolean }).__PORTAL_V2__ && (
          <FinishModal mode={mode} />
        )}
      {!embedParams.hideFooter && <IncompleteElementModal />}
    </div>
  )
}

export default Course
